/**
 * `/auth/tenant-select` — multi-tenant users pick which tenant to enter
 * after sign-in.
 *
 * Per KEN_LAYER_7 §8.1 step 2 (pre-tenant session): the shell renders only
 * /auth/* until tenant_id is established. GETs /api/shell/auth/tenants
 * (the only endpoint reachable with a pre-tenant JWT) to fetch the user's
 * tenant memberships, then POSTs the chosen one to /api/shell/auth/select-tenant
 * to mint the tenant-bound JWT.
 *
 * Per the hi-fi mockup at docs/ui/wireframes/01-login.html (state 05).
 */

import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@ken/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";

export const Route = createFileRoute("/auth/tenant-select")({
  component: TenantSelectRoute,
});

interface TenantOption {
  id: string;
  slug: string;
  name: string;
  vertical: string;
  role: string;
}

function TenantSelectRoute(): ReactElement {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/shell/auth/tenants", { credentials: "include" });
        if (cancelled) return;
        if (res.status === 401) {
          setError("Your session expired. Please sign in again.");
          return;
        }
        if (!res.ok) {
          setError(`Couldn't load tenants (status ${res.status}).`);
          return;
        }
        const body = (await res.json()) as TenantOption[];
        if (cancelled) return;
        setTenants(body);
        setSelectedId(body[0]?.id ?? "");
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Network error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onContinue = async (): Promise<void> => {
    if (!selectedId) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/shell/auth/select-tenant", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: selectedId }),
      });
      if (res.status === 403) {
        setError("You're not a member of that tenant.");
        return;
      }
      if (!res.ok) {
        setError(`Couldn't switch tenant (status ${res.status}).`);
        return;
      }
      // Tenant-bound JWT now set as the session cookie. Land on the tenant
      // root; the router redirects to the dashboard.
      const chosen = tenants.find((t) => t.id === selectedId);
      window.location.assign(`/${chosen?.slug ?? selectedId}/dashboard`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid place-items-center min-h-screen p-4 bg-[var(--color-bg-base)]">
      <Card surface="solid" className="w-full max-w-md" padding="lg">
        <CardHeader>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="grid place-items-center w-8 h-8 rounded-[var(--radius-md)] bg-[var(--color-brand)] text-white font-bold font-[var(--font-hand)] text-[22px]">
              K
            </div>
            <div className="font-[var(--font-hand)] text-[24px] font-semibold">ken</div>
          </div>
          <CardTitle>Choose a tenant</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div data-testid="auth-tenant-select-loading" className="flex flex-col gap-2.5">
              <Skeleton className="h-16 w-full rounded-[var(--radius-md)]" />
              <Skeleton className="h-16 w-full rounded-[var(--radius-md)]" />
            </div>
          ) : tenants.length === 0 ? (
            <div
              data-testid="auth-tenant-select-empty"
              className="text-sm text-[var(--color-text-soft)] py-8 text-center"
            >
              No tenant memberships found. Contact your admin.
            </div>
          ) : (
            <div
              role="radiogroup"
              aria-label="Tenants"
              data-testid="auth-tenant-select-list"
              className="flex flex-col gap-2.5"
            >
              {tenants.map((t) => {
                const selected = t.id === selectedId;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`auth-tenant-select-row-${t.slug}`}
                    onClick={() => setSelectedId(t.id)}
                    disabled={submitting}
                    className={[
                      "flex items-center justify-between px-3.5 py-3 rounded-[var(--radius-md)]",
                      "border-[1.4px] text-left transition-colors",
                      // Phase J: visible focus indicator (WCAG 2.4.7).
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[var(--color-brand-soft)]",
                      selected
                        ? "bg-[oklch(95%_0.04_250_/_0.7)] border-[var(--color-accent-blue)]"
                        : "bg-white border-[oklch(0%_0_0_/_0.30)] hover:bg-[oklch(98%_0.003_230)]",
                    ].join(" ")}
                  >
                    <div>
                      <div className="font-semibold text-sm">{t.name}</div>
                      <div className="text-xs text-[var(--color-text-soft)] mt-0.5">
                        {t.vertical} · {t.role}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {error !== null && (
            <div
              role="alert"
              data-testid="auth-tenant-select-error"
              className="mt-3 text-sm text-[var(--color-accent-red)] px-3 py-2 rounded-[var(--radius-sm)] bg-[oklch(99%_0.04_25_/_0.4)] border border-[var(--color-accent-red)]"
            >
              {error}
            </div>
          )}

          <Button
            type="button"
            variant="primary"
            data-testid="auth-tenant-select-continue-button"
            className="w-full mt-5"
            disabled={!selectedId || submitting || loading}
            onClick={() => void onContinue()}
          >
            {submitting ? "Continuing…" : "Continue"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
